import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpInterceptor,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, pluck, catchError, take } from 'rxjs/operators';
import { StoreService } from '@app/services/store/store.service';
import { MessageService } from 'primeng/api';

// HttpRequest objects are immutable, so in order to modify them, we need to
// first make a copy, then modify the copy and call handle on the modified copy

// Can be used for: notifications, toast errors, redirects ...
@Injectable()
export class StatusInterceptor implements HttpInterceptor {
  private redirectingForExpiredSession = false;

  constructor(
    private store: StoreService,
    private messageService: MessageService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.url.includes('api.zippopotam.us')) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      tap(event => {
        if (event instanceof HttpErrorResponse) {
          // console.log('Error', event.status, 'for :', event.url);
        } else if (event instanceof HttpResponse) {
          if (request.url.includes('DashUsers/authenticate') && event.status >= 200 && event.status < 300) {
            this.redirectingForExpiredSession = false;
          }
          // Just for debugging purpose
          // console.log('Status', event.status, 'for:', event.url);
        }
      }),
      catchError((error: any): Observable<any> => {
        // An empty rates table is an optional background-data condition, not a
        // page failure. Let the API service retain its cache without a toast.
        if (
          /\/CurrencyRates\/getCurrencyConversionRates(?:\?|$)/.test(request.url) &&
          error.status === 404 &&
          error.error?.error?.message === 'No currency rates available'
        ) {
          return throwError(() => error);
        }

        if (request.url.includes('DashUsers/authenticate')) {
          this.store.setIsLoginFailed(true);
          this.messageService.add({
            key: 'tst',
            severity: 'error',
            summary: 'Error',
            detail: `${error && error.status} ${
              (error && error.error && error.error.error && error.error.error.message) || ''
            }`,
          });
          return throwError(() => error);
        }

        const tokenErrorMessage = this.getErrorMessage(error);
        const isTokenFailure =
          error.status === 401 ||
          (error.status === 500 &&
            /(?:jwt|access\s*token|token).*(?:expired|invalid|malformed)|invalid.*token|expired.*token|unauthorized/i.test(
              tokenErrorMessage
            ));

        // Only inspect session state for authentication failures. Other HTTP
        // errors can come from services whose test doubles do not provide the
        // full StoreService, and must keep their original error handling.
        if (isTokenFailure && this.hasSession()) {
          if (!this.redirectingForExpiredSession) {
            this.redirectingForExpiredSession = true;
            this.messageService.add({
              key: 'tst',
              severity: 'warn',
              summary: 'Session expired',
              detail: 'Please sign in again.',
            });
            this.store.clearStoreAndLogout();
          }
          return throwError(() => error);
        }

        if (error.status == 0) {
          this.messageService.add({
            key: 'tst',
            severity: 'error',
            summary: 'Error',
            detail: 'Network Connection Error',
          });
        } else {
          this.messageService.add({
            key: 'tst',
            severity: 'error',
            summary: 'Error',
            detail: `${error && error.status} ${
              (error && error.error && error.error.error && error.error.error.message) || ''
            }`,
          });
        }

        return throwError(() => error);
      })
    );
  }

  private getErrorMessage(error: any): string {
    return error?.error?.error?.message || error?.error?.message || error?.message || '';
  }

  private hasSession(): boolean {
    try {
      return (
        !!this.store?.retrieveToken?.() ||
        (typeof localStorage !== 'undefined' && !!localStorage.getItem('impersonateToken'))
      );
    } catch {
      return false;
    }
  }
}

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private token: string;

  constructor(private store: StoreService) {
    this.store.state$.pipe(pluck('token')).subscribe(token => (this.token = token ? token.id : null));
  }

  /**
   * Always attach access token to url and headers
   * @param request
   * @param next
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.url.includes('.json') || request.url.includes('api.zippopotam.us')) {
      return next.handle(request);
    } else if (this.token) {
      const impersonateToken = localStorage.getItem('impersonateToken');
      const token = impersonateToken ? JSON.parse(impersonateToken).id : this.token;

      return next.handle(
        request.clone({
          url: `${request.url}${request.url.includes('?') ? '&' : '?'}access_token=${token}`,
          setHeaders: { Authorization: `Bearer ${token}` },
        })
      );
    } else {
      return next.handle(request);
    }
  }
}

@Injectable()
export class CacheInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(
      req.clone({
        setHeaders: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      })
    );
  }
}
